import React from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../components/AppIcon";
import { cn } from "../../utils/cn";
import ModuleBrochure, {
  type BrochureFeature,
  type BrochureMetric,
  type BrochureUseCase,
} from "./ModuleBrochure";

/**
 * Tournament brochure — Phase 6 build for federations and officiating orgs.
 *
 * Tone: "from bracket to payslip, without the spreadsheet detour". The pitch
 * is for a Chief Referee or federation operations lead who lives on a
 * 150-match weekend and is tired of WhatsApp groups full of assignment
 * questions and last-minute referee swaps.
 *
 * Illustration: an SVG single-elimination bracket — three matches drawn as
 * connected pill cards with team initials, plus a floating "Referee assigned"
 * pill and a subtle whistle motif behind it. Pure SVG + Tailwind; no stock.
 *
 * Mockup: a faux "Fixture board" — four scheduled matches with date, teams,
 * assigned referee (avatar chip), and a status pill. Right sidebar carries a
 * "This weekend" stat card so the page also tells a numerical story.
 */

// ----- Illustration: tournament bracket + "Referee assigned" pill + whistle motif -----

const TournamentIllustration: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";

  return (
    <div className="relative aspect-[4/5] w-full">
      {/* Background — soft orange wash with a faint whistle motif. The whistle
          stays large + low-opacity so it reads as atmosphere, not clip-art. */}
      <svg
        viewBox="0 0 320 400"
        className="absolute inset-0 h-full w-full"
        aria-hidden="true"
      >
        <defs>
          <radialGradient id="tnt-glow" cx="50%" cy="50%" r="55%">
            <stop offset="40%" stopColor="rgb(249 115 22 / 0.0)" />
            <stop offset="100%" stopColor="rgb(249 115 22 / 0.12)" />
          </radialGradient>
        </defs>
        <rect width="320" height="400" fill="url(#tnt-glow)" />

        {/* Whistle silhouette — a circle (whistle body) + a long rounded
            rectangle (mouthpiece) + a small chamber dot. Drawn at very low
            opacity in the trailing-bottom corner so the eye reads "officiating"
            without the illustration tipping into icon-soup. */}
        <g
          opacity="0.10"
          transform={isRtl ? "translate(60 300) scale(-1 1)" : "translate(220 300)"}
          fill="rgb(194 65 12)"
        >
          <circle cx="22" cy="22" r="22" />
          <rect x="40" y="14" width="32" height="16" rx="4" />
          <circle cx="22" cy="22" r="5" fill="white" />
        </g>

        {/* Connector lines between bracket rounds — drawn in SVG so we don't
            need to position pseudo-elements. The two left matches feed into
            the right "final" match. */}
        <g
          stroke="rgb(249 115 22 / 0.5)"
          strokeWidth="1.5"
          fill="none"
        >
          {/* From match A (top-left) → final */}
          <path d="M 130 110 H 165 V 200" />
          {/* From match B (bottom-left) → final */}
          <path d="M 130 290 H 165 V 200" />
        </g>
      </svg>

      {/* Match card — top-left (round 1, match A) */}
      <div className="absolute left-[6%] top-[18%] w-[40%] rounded-xl border border-slate-200 bg-white p-2.5 shadow-[0_12px_30px_-12px_rgba(15,23,42,0.25)]">
        <p className="text-[8px] font-semibold uppercase tracking-[0.14em] text-slate-400">
          {t("brochure.tournament.illustration.matchLabel", { num: 1 })}
        </p>
        <div className="mt-1.5 space-y-1">
          <div className="flex items-center justify-between text-[10px]">
            <span className="flex items-center gap-1.5">
              <span className="inline-flex h-4 w-4 items-center justify-center rounded-md bg-orange-100 text-[8px] font-bold text-orange-700">
                A
              </span>
              <span className="font-semibold text-slate-800">
                {t("brochure.tournament.illustration.teamA")}
              </span>
            </span>
            <span className="font-semibold tabular-nums text-slate-900">2</span>
          </div>
          <div className="flex items-center justify-between text-[10px]">
            <span className="flex items-center gap-1.5">
              <span className="inline-flex h-4 w-4 items-center justify-center rounded-md bg-slate-100 text-[8px] font-bold text-slate-600">
                B
              </span>
              <span className="text-slate-500">
                {t("brochure.tournament.illustration.teamB")}
              </span>
            </span>
            <span className="tabular-nums text-slate-500">1</span>
          </div>
        </div>
      </div>

      {/* Match card — bottom-left (round 1, match B) */}
      <div className="absolute left-[6%] top-[63%] w-[40%] rounded-xl border border-slate-200 bg-white p-2.5 shadow-[0_12px_30px_-12px_rgba(15,23,42,0.25)]">
        <p className="text-[8px] font-semibold uppercase tracking-[0.14em] text-slate-400">
          {t("brochure.tournament.illustration.matchLabel", { num: 2 })}
        </p>
        <div className="mt-1.5 space-y-1">
          <div className="flex items-center justify-between text-[10px]">
            <span className="flex items-center gap-1.5">
              <span className="inline-flex h-4 w-4 items-center justify-center rounded-md bg-slate-100 text-[8px] font-bold text-slate-600">
                C
              </span>
              <span className="text-slate-500">
                {t("brochure.tournament.illustration.teamC")}
              </span>
            </span>
            <span className="tabular-nums text-slate-500">0</span>
          </div>
          <div className="flex items-center justify-between text-[10px]">
            <span className="flex items-center gap-1.5">
              <span className="inline-flex h-4 w-4 items-center justify-center rounded-md bg-orange-100 text-[8px] font-bold text-orange-700">
                D
              </span>
              <span className="font-semibold text-slate-800">
                {t("brochure.tournament.illustration.teamD")}
              </span>
            </span>
            <span className="font-semibold tabular-nums text-slate-900">3</span>
          </div>
        </div>
      </div>

      {/* Match card — right (final). Highlighted with an orange ring so the
          eye knows where the bracket converges. */}
      <div className="absolute right-[6%] top-[40%] w-[40%] rounded-xl border border-orange-200 bg-white p-2.5 shadow-[0_16px_40px_-12px_rgba(249,115,22,0.35)] ring-1 ring-orange-200">
        <p className="text-[8px] font-semibold uppercase tracking-[0.14em] text-orange-700">
          {t("brochure.tournament.illustration.finalLabel")}
        </p>
        <div className="mt-1.5 space-y-1">
          <div className="flex items-center justify-between text-[10px]">
            <span className="flex items-center gap-1.5">
              <span className="inline-flex h-4 w-4 items-center justify-center rounded-md bg-orange-100 text-[8px] font-bold text-orange-700">
                A
              </span>
              <span className="font-semibold text-slate-800">
                {t("brochure.tournament.illustration.teamA")}
              </span>
            </span>
            <span className="text-[9px] text-slate-400">—</span>
          </div>
          <div className="flex items-center justify-between text-[10px]">
            <span className="flex items-center gap-1.5">
              <span className="inline-flex h-4 w-4 items-center justify-center rounded-md bg-orange-100 text-[8px] font-bold text-orange-700">
                D
              </span>
              <span className="font-semibold text-slate-800">
                {t("brochure.tournament.illustration.teamD")}
              </span>
            </span>
            <span className="text-[9px] text-slate-400">—</span>
          </div>
        </div>
      </div>

      {/* "Referee assigned" floating pill — anchored near the final, leans into
          the accent color so it's the loudest thing on the canvas. */}
      <div
        className={cn(
          "absolute top-[6%] flex items-center gap-1.5 rounded-full bg-orange-600 px-3 py-1.5",
          "text-[10px] font-semibold text-white shadow-[0_10px_24px_-8px_rgba(249,115,22,0.6)]",
          isRtl ? "left-[6%]" : "right-[6%]",
        )}
      >
        <Icon name="UserCheck" size={12} aria-hidden="true" />
        <span>{t("brochure.tournament.illustration.refereeAssigned")}</span>
      </div>
    </div>
  );
};

// ----- Mockup: faux "Fixture board" — 4 matches + "This weekend" sidebar -----

type MatchStatus = "scheduled" | "played" | "pending";

interface FixtureRow {
  dateKey: string;
  timeKey: string;
  homeKey: string;
  awayKey: string;
  homeInitials: string;
  awayInitials: string;
  refereeKey: string;
  refInitials: string;
  refTint: string;
  status: MatchStatus;
}

const FIXTURES: FixtureRow[] = [
  {
    dateKey: "brochure.tournament.mockup.fixture.f1.date",
    timeKey: "brochure.tournament.mockup.fixture.f1.time",
    homeKey: "brochure.tournament.mockup.team.alFaisal",
    awayKey: "brochure.tournament.mockup.team.alNasr",
    homeInitials: "AF",
    awayInitials: "AN",
    refereeKey: "brochure.tournament.mockup.referee.ahmad",
    refInitials: "AH",
    refTint: "bg-indigo-100 text-indigo-700",
    status: "scheduled",
  },
  {
    dateKey: "brochure.tournament.mockup.fixture.f2.date",
    timeKey: "brochure.tournament.mockup.fixture.f2.time",
    homeKey: "brochure.tournament.mockup.team.alHilal",
    awayKey: "brochure.tournament.mockup.team.alShabab",
    homeInitials: "AH",
    awayInitials: "AS",
    refereeKey: "brochure.tournament.mockup.referee.youssef",
    refInitials: "YA",
    refTint: "bg-emerald-100 text-emerald-700",
    status: "scheduled",
  },
  {
    dateKey: "brochure.tournament.mockup.fixture.f3.date",
    timeKey: "brochure.tournament.mockup.fixture.f3.time",
    homeKey: "brochure.tournament.mockup.team.alIttihad",
    awayKey: "brochure.tournament.mockup.team.alAhli",
    homeInitials: "AI",
    awayInitials: "AA",
    refereeKey: "brochure.tournament.mockup.referee.khaled",
    refInitials: "KS",
    refTint: "bg-violet-100 text-violet-700",
    status: "played",
  },
  {
    dateKey: "brochure.tournament.mockup.fixture.f4.date",
    timeKey: "brochure.tournament.mockup.fixture.f4.time",
    homeKey: "brochure.tournament.mockup.team.alWehda",
    awayKey: "brochure.tournament.mockup.team.alTaawoun",
    homeInitials: "AW",
    awayInitials: "AT",
    refereeKey: "brochure.tournament.mockup.referee.unassigned",
    refInitials: "?",
    refTint: "bg-slate-100 text-slate-400",
    status: "pending",
  },
];

const STATUS_STYLES: Record<MatchStatus, { dot: string; text: string; bg: string; ring: string }> = {
  scheduled: {
    dot: "bg-indigo-500",
    text: "text-indigo-700",
    bg: "bg-indigo-50",
    ring: "ring-indigo-200",
  },
  played: {
    dot: "bg-emerald-500",
    text: "text-emerald-700",
    bg: "bg-emerald-50",
    ring: "ring-emerald-200",
  },
  pending: {
    dot: "bg-amber-500",
    text: "text-amber-800",
    bg: "bg-amber-50",
    ring: "ring-amber-200",
  },
};

const TournamentMockup: React.FC = () => {
  const { t } = useTranslation();
  const statusLabelKey: Record<MatchStatus, string> = {
    scheduled: "brochure.tournament.mockup.status.scheduled",
    played: "brochure.tournament.mockup.status.played",
    pending: "brochure.tournament.mockup.status.pending",
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_220px]">
      {/* Fixture board */}
      <div className="overflow-hidden rounded-lg border border-slate-200">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <Icon name="Trophy" size={14} className="text-slate-500" aria-hidden="true" />
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
              {t("brochure.tournament.mockup.boardHeading")}
            </p>
          </div>
          <p className="text-[10px] font-medium text-slate-400">
            {t("brochure.tournament.mockup.boardRange")}
          </p>
        </div>

        <ul className="divide-y divide-slate-100 bg-white">
          {FIXTURES.map((f, i) => {
            const s = STATUS_STYLES[f.status];
            return (
              <li key={i} className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-4 py-3">
                {/* Date/time column */}
                <div className="leading-tight">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                    {t(f.dateKey)}
                  </p>
                  <p className="text-[10px] tabular-nums text-slate-500">{t(f.timeKey)}</p>
                </div>

                {/* Teams + referee */}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-slate-100 text-[9px] font-bold text-slate-700">
                      {f.homeInitials}
                    </span>
                    <span className="font-semibold text-slate-900">{t(f.homeKey)}</span>
                    <span className="text-slate-300">·</span>
                    <span className="font-semibold text-slate-900">{t(f.awayKey)}</span>
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-slate-100 text-[9px] font-bold text-slate-700">
                      {f.awayInitials}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-1.5 text-[10px] text-slate-500">
                    <span
                      className={cn(
                        "inline-flex h-4 w-4 items-center justify-center rounded-full text-[8px] font-bold",
                        f.refTint,
                      )}
                    >
                      {f.refInitials}
                    </span>
                    <span>{t(f.refereeKey)}</span>
                  </div>
                </div>

                {/* Status pill */}
                <span
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1",
                    s.bg,
                    s.text,
                    s.ring,
                  )}
                >
                  <span className={cn("h-1.5 w-1.5 rounded-full", s.dot)} />
                  {t(statusLabelKey[f.status])}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* "This weekend" sidebar */}
      <div className="grid gap-3">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            {t("brochure.tournament.mockup.weekend.heading")}
          </p>
          <div className="mt-2 space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-slate-600">
                <Icon name="CalendarDays" size={12} className="text-slate-400" aria-hidden="true" />
                {t("brochure.tournament.mockup.weekend.matches")}
              </span>
              <span className="font-semibold tabular-nums text-slate-900">12</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-slate-600">
                <Icon name="Users" size={12} className="text-slate-400" aria-hidden="true" />
                {t("brochure.tournament.mockup.weekend.referees")}
              </span>
              <span className="font-semibold tabular-nums text-slate-900">8</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-slate-600">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                {t("brochure.tournament.mockup.weekend.unassigned")}
              </span>
              <span className="font-semibold tabular-nums text-emerald-700">0</span>
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-orange-700">
            {t("brochure.tournament.mockup.payouts.heading")}
          </p>
          <p className="mt-1.5 text-2xl font-semibold tabular-nums text-orange-900">
            {t("brochure.tournament.mockup.payouts.amount")}
          </p>
          <p className="mt-0.5 text-[10px] leading-snug text-orange-800/80">
            {t("brochure.tournament.mockup.payouts.hint")}
          </p>
        </div>
      </div>
    </div>
  );
};

// ----- Brochure props ------

const TournamentBrochure: React.FC = () => {
  const { t } = useTranslation();

  const features: BrochureFeature[] = [
    {
      icon: "Trophy",
      title: t("brochure.tournament.features.setup.title"),
      description: t("brochure.tournament.features.setup.description"),
    },
    {
      icon: "CalendarClock",
      title: t("brochure.tournament.features.scheduling.title"),
      description: t("brochure.tournament.features.scheduling.description"),
    },
    {
      icon: "UserCheck",
      title: t("brochure.tournament.features.refereeAssignment.title"),
      description: t("brochure.tournament.features.refereeAssignment.description"),
    },
    {
      icon: "Coins",
      title: t("brochure.tournament.features.workPoints.title"),
      description: t("brochure.tournament.features.workPoints.description"),
    },
    {
      icon: "BarChart3",
      title: t("brochure.tournament.features.workloadAnalytics.title"),
      description: t("brochure.tournament.features.workloadAnalytics.description"),
    },
    {
      icon: "Store",
      title: t("brochure.tournament.features.marketplace.title"),
      description: t("brochure.tournament.features.marketplace.description"),
    },
  ];

  const metrics: BrochureMetric[] = [
    {
      value: "85%",
      label: t("brochure.tournament.metrics.conflictsLabel"),
    },
    {
      value: t("brochure.tournament.metrics.sourceValue"),
      label: t("brochure.tournament.metrics.sourceLabel"),
    },
    {
      value: t("brochure.tournament.metrics.hoursValue"),
      label: t("brochure.tournament.metrics.hoursLabel"),
    },
  ];

  const useCases: BrochureUseCase[] = [
    {
      persona: t("brochure.tournament.useCases.federation.persona"),
      scenario: t("brochure.tournament.useCases.federation.scenario"),
      outcome: t("brochure.tournament.useCases.federation.outcome"),
    },
    {
      persona: t("brochure.tournament.useCases.chief.persona"),
      scenario: t("brochure.tournament.useCases.chief.scenario"),
      outcome: t("brochure.tournament.useCases.chief.outcome"),
    },
  ];

  return (
    <ModuleBrochure
      slug="tournament"
      hero={{
        eyebrow: t("brochure.tournament.hero.eyebrow"),
        headline: t("brochure.tournament.hero.headline"),
        subheadline: t("brochure.tournament.hero.subheadline"),
        illustration: <TournamentIllustration />,
      }}
      features={features}
      metrics={metrics}
      mockup={{
        title: t("brochure.tournament.mockup.caption"),
        content: <TournamentMockup />,
      }}
      useCases={useCases}
    />
  );
};

export default TournamentBrochure;
